import { EntityServiceAPI } from 'api/EntityServiceAPI';

interface BusinessVerificationRecord {
  association_id?: string | number;
  database_entity_id?: string | number;
  database_id?: string | number;
}

export type VerificationRunCheckStatus =
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'unavailable';

/** One Verification Check's outcome in a run, as the entity service reports it. */
export interface VerificationRunCheck {
  verificationType: string;
  status: VerificationRunCheckStatus;
  reason?: string;
}

/** Why the service would not run, mapped from its status and `code`. */
export type VerificationRunRefusal =
  | 'addon-required'
  | 'database-off'
  | 'nothing-to-verify'
  | 'in-progress'
  | 'no-credits'
  | 'not-found'
  | 'membership-unavailable'
  | 'unreachable';

export type VerificationRunOutcome =
  | { ok: true; checks: VerificationRunCheck[] }
  | { ok: false; refusal: VerificationRunRefusal; message: string };

type VerificationRunResponse =
  | {
      success: boolean;
      status: number;
      code?: string;
      result: any;
    }
  | undefined;

interface VerificationRunApi {
  runBusinessVerification: (payload: {
    databaseId: string;
    databaseEntityId: string;
  }) => Promise<VerificationRunResponse>;
}

const optionalText = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
};

const REFUSALS_BY_CODE: Record<string, VerificationRunRefusal> = {
  ENTITY_ADDON_REQUIRED: 'addon-required',
  DATABASE_VERIFICATION_OFF: 'database-off',
  NOTHING_TO_VERIFY: 'nothing-to-verify',
  RUN_IN_PROGRESS: 'in-progress',
  // The run's credit gate (403) and its per-check reservation (402).
  VERIFICATION_CREDITS_EXHAUSTED: 'no-credits',
  NO_VERIFICATION_CREDITS: 'no-credits',
  MEMBERSHIP_UNAVAILABLE: 'membership-unavailable',
};

/**
 * The verification page's check rows each Verification Check feeds. The page
 * groups domain age and reputation into one row, and one location lookup
 * answers two rows.
 */
export const CHECK_IDS_BY_VERIFICATION_TYPE: Record<string, string[]> = {
  domain_age: ['domain-contact'],
  domain_reputation: ['domain-contact'],
  website_quality: ['website-contact'],
  email_verify: ['email-verification'],
  phone_verify: ['phone-validation'],
  places_verification: ['listing-consistency', 'address-facility'],
  cbr_registry: ['business-registration'],
};

/** Check rows that kept an earlier result because their lookup did not answer. */
export const staleCheckIds = (checks: VerificationRunCheck[]): string[] =>
  Array.from(
    new Set(
      checks
        .filter(
          (check) =>
            check.status === 'failed' || check.status === 'unavailable',
        )
        .flatMap(
          (check) =>
            CHECK_IDS_BY_VERIFICATION_TYPE[check.verificationType] || [],
        ),
    ),
  );

/**
 * Starts a Verification Run for one Database Entity. The entity service reads
 * the record's details itself, runs every check they allow (except adverse
 * media), saves each result to the record and answers once all are done.
 */
export const runBusinessVerification = async (
  record: BusinessVerificationRecord,
  api: VerificationRunApi = EntityServiceAPI,
): Promise<VerificationRunOutcome> => {
  const databaseEntityId = optionalText(
    record.association_id ?? record.database_entity_id,
  );
  const databaseId = optionalText(record.database_id);
  if (!databaseEntityId || !databaseId) {
    return {
      ok: false,
      refusal: 'not-found',
      message: 'This record is not in a database, so it cannot be verified.',
    };
  }

  // The client turns HTTP failures into a response; anything it still throws
  // (it could not even build the request) is the service being unreachable.
  let response: VerificationRunResponse;
  try {
    response = await api.runBusinessVerification({
      databaseId: databaseId,
      databaseEntityId: databaseEntityId,
    });
  } catch {
    response = undefined;
  }
  if (response?.success) {
    return {
      ok: true,
      checks: Array.isArray(response.result?.checks)
        ? response.result.checks
        : [],
    };
  }

  const message =
    typeof response?.result === 'string' && response.result
      ? response.result
      : 'Verification could not start. Check your connection and try again.';
  const byCode = response?.code ? REFUSALS_BY_CODE[response.code] : undefined;
  if (byCode) return { ok: false, refusal: byCode, message: message };
  if (response?.status === 404) {
    return { ok: false, refusal: 'not-found', message: message };
  }
  // Timeouts and network errors carry axios wording; say what happened instead.
  return {
    ok: false,
    refusal: 'unreachable',
    message:
      'The verification service did not respond, so nothing was updated. Try again in a moment.',
  };
};
